import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatXOF } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Clock, Video, Headphones, MessageSquare, CheckCircle2, AlertCircle, ListChecks, PlayCircle } from "lucide-react";

const STATUS_LABEL = {
  pending: "En attente",
  confirmed: "Confirmé",
  completed: "Terminé",
  cancelled: "Annulé",
};
const STATUS_STYLE = {
  pending: "bg-gold-light/40 text-gold-dark border border-gold/40",
  confirmed: "bg-sage-50 text-sage-700 border border-sage-200",
  completed: "bg-beige border border-border text-ink-soft",
  cancelled: "bg-red-50 text-red-700 border border-red-200",
};
const MODE_ICON = { video: Video, audio: Headphones, chat: MessageSquare };

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/me");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const upcoming = items.filter((a) => a.status === "pending" || a.status === "confirmed");
  const past = items.filter((a) => a.status === "completed" || a.status === "cancelled");

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      fetch();
    } catch (e) { /* noop */ }
  };

  return (
    <div className="container-jamm py-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Mon espace</div>
          <h1 className="h-display mt-3">Bonjour, <span className="italic text-sage-600">{user?.name?.split(" ")[0]}</span></h1>
          <p className="lead mt-2">Voici votre espace privé. Toutes vos séances, en un coup d'œil.</p>
        </div>
        <Link to="/accompagnateurs" className="btn-primary" data-testid="dashboard-new-rdv">Nouvelle séance</Link>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-10">
        {[
          { label: "À venir", value: upcoming.length, icon: Calendar, color: "text-sage-700 bg-sage-50" },
          { label: "Terminées", value: items.filter(i => i.status === "completed").length, icon: CheckCircle2, color: "text-gold-dark bg-gold-light/40" },
          { label: "Total", value: items.length, icon: ListChecks, color: "text-ink bg-beige" },
        ].map((s) => (
          <div key={s.label} className="card-soft flex items-center gap-4" data-testid={`stat-${s.label}`}>
            <div className={`grid place-items-center w-12 h-12 rounded-2xl ${s.color}`}><s.icon className="w-5 h-5" strokeWidth={1.5} /></div>
            <div>
              <div className="text-ink-mute text-xs uppercase tracking-wider">{s.label}</div>
              <div className="font-display text-3xl text-ink leading-none mt-1">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="font-display text-2xl text-ink">Prochains rendez-vous</h2>
        <div className="mt-4 grid gap-4">
          {loading ? (
            <div className="card-soft animate-pulse h-24 bg-beige/40" />
          ) : upcoming.length === 0 ? (
            <div className="card-soft text-ink-soft text-center py-10" data-testid="dashboard-empty-upcoming">
              <AlertCircle className="w-6 h-6 text-ink-mute mx-auto mb-2" />
              Aucun rendez-vous à venir. <Link to="/accompagnateurs" className="text-sage-600 hover:underline">Réserver une séance</Link>
            </div>
          ) : (
            upcoming.map((ap) => {
              const Icon = MODE_ICON[ap.mode] || Video;
              return (
                <div key={ap.id} className="card-soft flex flex-wrap items-center justify-between gap-4" data-testid={`appointment-${ap.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    {ap.accompagnateur_avatar && <img src={ap.accompagnateur_avatar} alt={ap.accompagnateur_name} className="w-12 h-12 rounded-full object-cover ring-2 ring-sage-100" />}
                    <div className="min-w-0">
                      <div className="font-display text-xl text-ink truncate">{ap.accompagnateur_name}</div>
                      <div className="text-ink-soft text-sm flex flex-wrap items-center gap-3">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {ap.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {ap.time} · {ap.duration_min} min</span>
                        <span className="flex items-center gap-1"><Icon className="w-3.5 h-3.5 capitalize" /> {ap.mode}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${STATUS_STYLE[ap.status]}`}>{STATUS_LABEL[ap.status]}</span>
                    <span className="font-display text-xl text-sage-700">{formatXOF(ap.price)}</span>
                    {ap.mode !== "chat" && ap.status !== "cancelled" && ap.jitsi_url && (
                      <a
                        href={ap.jitsi_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary !py-1.5 !px-4 text-xs"
                        data-testid={`join-${ap.id}`}
                      >
                        <PlayCircle className="w-3.5 h-3.5" /> Rejoindre la séance
                      </a>
                    )}
                    {ap.status !== "cancelled" && (
                      <button onClick={() => updateStatus(ap.id, "cancelled")} className="btn-outline !py-1.5 !px-4 text-xs" data-testid={`cancel-${ap.id}`}>Annuler</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl text-ink">Historique</h2>
        <div className="mt-4 grid gap-4">
          {past.length === 0 ? (
            <div className="card-soft text-ink-soft text-center py-8 text-sm" data-testid="dashboard-empty-history">Pas encore d'historique.</div>
          ) : (
            past.map((ap) => (
              <div key={ap.id} className="card-soft flex flex-wrap items-center justify-between gap-4" data-testid={`history-${ap.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  {ap.accompagnateur_avatar && <img src={ap.accompagnateur_avatar} alt="" className="w-12 h-12 rounded-full object-cover" />}
                  <div className="min-w-0">
                    <div className="font-display text-lg text-ink truncate">{ap.accompagnateur_name}</div>
                    <div className="text-ink-mute text-sm">{ap.date} · {ap.time} · {ap.duration_min} min</div>
                  </div>
                </div>
                <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${STATUS_STYLE[ap.status]}`}>{STATUS_LABEL[ap.status]}</span>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
